import React, { useEffect, useRef, useState } from 'react';
import { UserItem } from './types';

interface UserActionsMenuProps {
  user: UserItem;
  currentUserId?: string;
  onImpersonate: (user: UserItem) => void;
  onEdit: (user: UserItem) => void;
  onResetPassword: (user: UserItem) => void;
  onDelete: (user: UserItem) => void;
}

const UserActionsMenu: React.FC<UserActionsMenuProps> = ({ user, currentUserId, onImpersonate, onEdit, onResetPassword, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const canImpersonate = user.role !== 'admin' && user.id !== currentUserId;
  const run = (fn: () => void) => () => { setOpen(false); fn(); };
  const itemClass = 'block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50';

  return (
    <div className="relative inline-block text-left" ref={ref}>
      <button
        type="button"
        aria-label="User actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200"
      >
        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-md bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 shadow-lg py-1">
          {canImpersonate && (
            <button role="menuitem" onClick={run(() => onImpersonate(user))} className={`${itemClass} text-purple-600 dark:text-purple-400`}>Impersonate</button>
          )}
          <button role="menuitem" onClick={run(() => onEdit(user))} className={`${itemClass} text-gray-700 dark:text-gray-200`}>Edit</button>
          <button role="menuitem" onClick={run(() => onResetPassword(user))} className={`${itemClass} text-gray-700 dark:text-gray-200`}>Reset Password</button>
          <button role="menuitem" onClick={run(() => onDelete(user))} className={`${itemClass} text-red-600 dark:text-red-400`}>Delete</button>
        </div>
      )}
    </div>
  );
};

export default UserActionsMenu;
